import React, { useState, useEffect } from 'react';
import { Folder, Document } from '../../types';
import { folderService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFolderNavigation } from '../../contexts/FolderNavigationContext';
import FolderCard from './components/FolderCard';
import DocumentCard from './components/DocumentCard';
import CreateFolderModal from './components/CreateFolderModal';
import UploadDocumentModal from './components/UploadDocumentModal';
import './Documentation.css';
import background from "../../assets/caja.webp";

const DocumentationMain: React.FC = () => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFolders, setFilteredFolders] = useState<Folder[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);

  const { state: authState } = useAuth();
  const { 
    selectedFolder, 
    folderHistory, 
    setSelectedFolder, 
    setFolderHistory,
    handleFolderClick, 
    handleBackToFolders, 
    handleBreadcrumbClick,
    setSidebarKey 
  } = useFolderNavigation();

  useEffect(() => {
    if (selectedFolder) {
      // Cuando estamos dentro de una carpeta
      loadDocuments(selectedFolder.pk_folder);
      
      // Si es una carpeta principal (sin parent), mostrar subcarpetas
      if (!selectedFolder.parent_folder) {
        loadFolders(selectedFolder.pk_folder);
      } else {
        // Si es una subcarpeta, no mostrar más carpetas
        setFolders([]);
      }
    } else {
      // Cuando estamos en la vista principal, cargar carpetas de nivel raíz
      loadFolders(null);
      setDocuments([]);
    }
  }, [selectedFolder]);

  // Efecto para filtrar contenido basado en la búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredFolders(folders);
      setFilteredDocuments(documents);
    } else {
      const searchLower = searchTerm.toLowerCase();
      setFilteredFolders(
        folders.filter(folder => 
          folder.name.toLowerCase().includes(searchLower)
        )
      );
      setFilteredDocuments(
        documents.filter(document => 
          document.name.toLowerCase().includes(searchLower)
        )
      );
    }
  }, [searchTerm, folders, documents]);

  const loadFolders = async (parentFolderId?: number | null, updateHistory: boolean = true) => {
    try {
      setLoading(true);
      const foldersData = await folderService.getFolders(parentFolderId);
      setFolders(foldersData);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async (folderId: number) => {
    try {
      const documentsData = await folderService.getFolderDocuments(folderId);
      setDocuments(documentsData);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleDocumentDeleted = () => {
    // Recargar documentos de la carpeta actual
    if (selectedFolder) {
      loadDocuments(selectedFolder.pk_folder);
    }
  };

  const handleFolderCreated = () => {
    // Recargar carpetas del nivel actual
    if (selectedFolder && !selectedFolder.parent_folder) {
      // Estamos en carpeta principal, recargar subcarpetas
      loadFolders(selectedFolder.pk_folder);
    } else if (!selectedFolder) {
      // Estamos en vista principal, recargar carpetas raíz
      loadFolders(null);
    }
    // Refrescar sidebar
    setSidebarKey(Date.now());
    setShowCreateFolder(false);
  };

  const handleDocumentUploaded = () => {
    if (selectedFolder) {
      loadDocuments(selectedFolder.pk_folder);
    }
    setShowUploadDocument(false);
  };



  const handleFolderUpdated = (updatedFolder: Folder) => {
    // Actualizar la lista de carpetas con la carpeta editada
    setFolders(prevFolders => 
      prevFolders.map(folder => 
        folder.pk_folder === updatedFolder.pk_folder ? updatedFolder : folder
      )
    );
    
    // Si la carpeta editada es la que está seleccionada, también actualizarla
    if (selectedFolder && selectedFolder.pk_folder === updatedFolder.pk_folder) {
      setSelectedFolder(updatedFolder);
    }

    // Actualizar el historial si la carpeta editada está en él
    setFolderHistory(
      folderHistory.map(folder =>
        folder.pk_folder === updatedFolder.pk_folder ? updatedFolder : folder
      )
    );

    // Refrescar sidebar
    setSidebarKey(Date.now());
  };



  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
        <div style={{ fontSize: '1.125rem', color: '#6b7280' }}>Cargando...</div>
      </div>
    );
  }

  return (
    <div className="documentation-main-container" style={{ backgroundImage: `url(${background})` }}>
      {/* Page Header */}
      <div style={{ marginBottom: '1.5rem' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedFolder ? (
                  (() => {
                    // Mostrar la ruta completa estilo GitHub
                    const pathParts = selectedFolder.path.split('/').filter(part => part && part !== authState.user?.username);
                    return pathParts.join(' / ');
                  })()
                ) : 'Mis Documentos'}
              </h1>
              <p className="text-gray-600 mt-1">
                {selectedFolder 
                  ? `Documentos en ${selectedFolder.name}`
                  : 'Organiza y gestiona todos tus documentos'
                }
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Buscador */}
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Buscar carpetas y documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="search-clear"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Mostrar botón Nueva Carpeta solo para administradores y si:
                  1. Estamos en vista principal (!selectedFolder) 
                  2. O estamos en carpeta principal (selectedFolder sin parent) */}
              {authState.user?.role === 'admin' && (!selectedFolder || (selectedFolder && !selectedFolder.parent_folder)) && (
                <button
                  onClick={() => setShowCreateFolder(true)}
                  className="btn-compact btn-primary"
                >
                  + Nueva Carpeta
                </button>
              )}
              {authState.user?.role === 'admin' && selectedFolder && (
                <button
                  onClick={() => setShowUploadDocument(true)}
                  className="btn btn-success gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Subir Archivo
                </button>
              )}
            </div>
          </div>

          {/* Breadcrumb estilo moderno con navegación clickeable */}
          {selectedFolder && (
            <nav className="breadcrumb-nav">
              <button 
                onClick={handleBackToFolders}
                className="breadcrumb-item breadcrumb-button"
              >
                BBX-Documentation
              </button>
              
              <span className="breadcrumb-separator">/</span>
              
              {/* Mostrar breadcrumb basado en el historial de navegación */}
              {folderHistory.map((folder, index) => (
                <React.Fragment key={folder.pk_folder}>
                  {index === folderHistory.length - 1 ? (
                    // Último elemento (carpeta actual) - no clickeable
                    <span className="breadcrumb-item breadcrumb-current">{folder.name}</span>
                  ) : (
                    // Elementos intermedios - clickeables
                    <>
                      <button
                        onClick={() => handleBreadcrumbClick(index + 1)}
                        className="breadcrumb-item breadcrumb-button"
                      >
                        {folder.name}
                      </button>
                      <span className="breadcrumb-separator">/</span>
                    </>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>

        {/* Content Grid */}
        {/* Indicador de búsqueda activa */}
        {searchTerm.trim() !== '' && (
          <div className="search-results-indicator">
            <div className="search-results-header">
              <div className="search-results-text">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Mostrando resultados para: <strong>"{searchTerm}"</strong>
              </div>
              <button
                onClick={() => setSearchTerm('')}
                className="clear-search-btn"
              >
                Limpiar búsqueda
              </button>
            </div>
            <p className="search-results-count">
              {filteredFolders.length} carpeta{filteredFolders.length !== 1 ? 's' : ''} y {filteredDocuments.length} documento{filteredDocuments.length !== 1 ? 's' : ''} encontrado{filteredFolders.length + filteredDocuments.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {!selectedFolder ? (
            // Vista principal: mostrar solo carpetas de nivel raíz
            filteredFolders.length > 0 ? (
              filteredFolders.map((folder) => (
                <FolderCard
                  key={folder.pk_folder}
                  folder={folder}
                  onClick={() => handleFolderClick(folder)}
                  onFolderUpdated={handleFolderUpdated}
                />
              ))
            ) : searchTerm.trim() !== '' ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <div className="empty-state-icon">
                  <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron resultados</h3>
                <p className="text-gray-500 mb-4">
                  No hay carpetas que coincidan con "{searchTerm}"
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="btn btn-primary"
                >
                  Limpiar búsqueda
                </button>
              </div>
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <div className="empty-state-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-folder" viewBox="0 0 16 16">
                    <path d="M.54 3.87.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.826a2 2 0 0 1-1.991-1.819l-.637-7a2 2 0 0 1 .342-1.31zM2.19 4a1 1 0 0 0-.996 1.09l.637 7a1 1 0 0 0 .995.91h10.348a1 1 0 0 0 .995-.91l.637-7A1 1 0 0 0 13.81 4zm4.69-1.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981l.006.139q.323-.119.684-.12h5.396z"/>
                  </svg>
                </div>  
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes carpetas aún</h3>
                <p className="text-gray-500 mb-4">
                  {authState.user?.role === 'admin' 
                    ? 'Crea tu primera carpeta para organizar tus documentos'
                    : 'No hay carpetas disponibles todavía'
                  }
                </p>
                {authState.user?.role === 'admin' && (
                  <button
                    onClick={() => setShowCreateFolder(true)}
                    className="btn btn-primary"
                  >
                    Crear Carpeta
                  </button>
                )}
              </div>
            )
          ) : (
            // Vista dentro de carpeta: mostrar subcarpetas (si es carpeta principal) y documentos
            <>
              {/* Subcarpetas (solo si estamos en una carpeta principal) */}
              {!selectedFolder.parent_folder && filteredFolders.map((folder) => (
                <FolderCard
                  key={folder.pk_folder}
                  folder={folder}
                  onClick={() => handleFolderClick(folder)}
                  onFolderUpdated={handleFolderUpdated}
                />
              ))}
              
              {/* Documentos */}
              {filteredDocuments.map((document) => (
                <DocumentCard
                  key={document.pk_document}
                  document={document}
                  currentUser={authState.user}
                  onDocumentDeleted={handleDocumentDeleted}
                  folderId={selectedFolder?.pk_folder || 0}
                />
              ))}
              
              {/* Estado vacío o sin resultados de búsqueda */}
              {searchTerm.trim() !== '' && filteredFolders.length === 0 && filteredDocuments.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center" style={{ minHeight: '400px' }}>
                  <div className="empty-state-icon">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron resultados</h3>
                  <p className="text-gray-500 mb-4">
                    No hay elementos en esta carpeta que coincidan con "{searchTerm}"
                  </p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="btn btn-primary"
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              ) : (!selectedFolder.parent_folder ? filteredFolders.length === 0 && filteredDocuments.length === 0 : filteredDocuments.length === 0) && searchTerm.trim() === '' ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-center" style={{ minHeight: '400px' }}>
                  <div className="empty-state-icon">
                    <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#d1d5db' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Esta carpeta está vacía</h3>
                  <p className="text-gray-500 mb-4">
                    {authState.user?.role === 'admin' 
                      ? (!selectedFolder.parent_folder 
                          ? `Crea una subcarpeta o sube tu primer documento a ${selectedFolder.name}`
                          : `Sube tu primer documento a ${selectedFolder.name}`
                        )
                      : `Esta carpeta está vacía`
                    }
                  </p>
                  {authState.user?.role === 'admin' && (
                    <div className="flex gap-3">
                      {!selectedFolder.parent_folder && (
                        <button
                          onClick={() => setShowCreateFolder(true)}
                          className="btn btn-primary"
                        >
                          Nueva Carpeta
                        </button>
                      )}
                      <button
                        onClick={() => setShowUploadDocument(true)}
                        className="btn btn-success"
                      >
                        Subir Archivo
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>

            {/* Modals */}
            {showCreateFolder && (
              <CreateFolderModal
                onClose={() => setShowCreateFolder(false)}
                onSuccess={handleFolderCreated}
                parentFolderId={selectedFolder?.pk_folder || null}
              />
            )}

        {showUploadDocument && selectedFolder && (
          <UploadDocumentModal
            folderId={selectedFolder.pk_folder}
            onClose={() => setShowUploadDocument(false)}
            onSuccess={handleDocumentUploaded}
          />
        )}
      </div>
  );
};

export default DocumentationMain;